/**
 * ORION — Observability Domain
 *
 * File: ReplayTimeline.tsx
 *
 * Responsibility:
 * - Displays the chronological history of a Cognitive Object.
 * - Handles Real-Time updates via SSE (utilizing custom hooks).
 * - Visualizes events like Decisions, Transitions, and Votes.
 *
 * What this file does NOT do:
 * - Does not persist events.
 * - Does not decide what events are valid.
 *
 * Depends on:
 * - observabilityService (Historical Data)
 * - useObjectStream (Real-time Data)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { observabilityService } from '../observabilityService';
import type { CognitiveEvent } from '../observabilityService';
import { useObjectStream } from '../useObjectStream';

interface ReplayTimelineProps {
    objectId: string;
}

export const ReplayTimeline: React.FC<ReplayTimelineProps> = ({ objectId }) => {
    const [events, setEvents] = useState<CognitiveEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Initial Load
    useEffect(() => {
        loadReplay();
    }, [objectId]);

    const loadReplay = async () => {
        try {
            setLoading(true);
            const data = await observabilityService.getReplay(objectId);
            // Ensure sorting by timestamp
            const sorted = sortEvents(data);
            setEvents(sorted);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Failed to load timeline');
        } finally {
            setLoading(false);
        }
    };

    // Real-time Stream Handler
    const handleNewEvent = useCallback((newEvent: CognitiveEvent) => {
        setEvents(prev => {
            // Avoid duplicates
            if (prev.some(e => e.event_id === newEvent.event_id)) return prev;
            // Append and sort
            return sortEvents([...prev, newEvent]);
        });
    }, []);

    // Connect SSE
    const { status } = useObjectStream(objectId, handleNewEvent);

    const sortEvents = (list: CognitiveEvent[]) => {
        return list.sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
    };

    if (loading) {
        return <div className="p-4 text-center text-gray-500 text-xs animate-pulse">Loading execution history...</div>;
    }

    if (error) {
        return (
            <div className="p-4 border border-red-800/50 bg-red-900/10 rounded text-center">
                <p className="text-red-400 text-sm mb-2">Failed to load timeline</p>
                <button
                    onClick={loadReplay}
                    className="text-xs text-blue-400 hover:text-blue-300 underline"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="relative pl-4 space-y-6">
            {/* Stream Status Indicator */}
            <div className="flex justify-between items-center text-xs mb-4 border-b border-gray-800 pb-2">
                <span className="text-gray-500 font-mono tracking-wider">LIVE STREAM</span>
                <div className="flex items-center space-x-2">
                    <span className={`w-2 h-2 rounded-full transition-colors duration-500 ${status === 'OPEN' ? 'bg-green-500 shadow-[0_0_5px_#22c55e]' :
                        status === 'CONNECTING' ? 'bg-yellow-500 animate-pulse' :
                            'bg-red-500'
                        }`}></span>
                    <span className={`font-mono font-bold ${status === 'OPEN' ? 'text-green-500' :
                        status === 'CONNECTING' ? 'text-yellow-500' :
                            'text-red-500'
                        }`}>
                        {status === 'OPEN' ? 'ONLINE' : status}
                    </span>
                </div>
            </div>

            {/* Vertical Line */}
            <div className="absolute left-1.5 top-12 bottom-2 w-0.5 bg-gray-800"></div>

            {events.length === 0 && (
                <div className="text-gray-600 italic text-sm text-center py-4">
                    No events recorded. Waiting for activity...
                </div>
            )}

            {events.map((event, index) => (
                <div key={event.event_id || index} className="relative pl-6 animate-in fade-in slide-in-from-left-2 duration-300">
                    {/* Dot */}
                    <div className={`
                        absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full border-2 border-slate-900 z-10
                        ${getEventColor(event.event_type)}
                    `}></div>

                    {/* Content */}
                    <div className="text-sm">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-xs text-gray-500">
                                {new Date(event.timestamp).toLocaleTimeString()}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase ${getEventBadge(event.event_type)}`}>
                                {event.event_type}
                            </span>
                        </div>

                        {/* Payload Details */}
                        <div className="bg-slate-900/50 rounded border border-slate-800 p-2 text-xs font-mono text-gray-400">
                            {renderPayloadSummary(event)}

                            <details className="mt-1 group">
                                <summary className="cursor-pointer text-gray-600 hover:text-gray-500 select-none list-none text-[10px] mt-1">
                                    ▶ REMOTE PAYLOAD
                                </summary>
                                <pre className="mt-2 overflow-x-auto text-[10px] text-gray-500 p-1 bg-black/20 rounded">
                                    {JSON.stringify(event.payload, null, 2)}
                                </pre>
                            </details>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

// UI Helpers
function getEventColor(type: string): string {
    if (type.includes('ERROR')) return 'bg-red-500';
    if (type.includes('DECISION')) return 'bg-green-500';
    if (type.includes('VOTE')) return 'bg-blue-500';
    if (type.includes('TRANSITION')) return 'bg-purple-500';
    if (type.includes('CREATED')) return 'bg-gray-200';
    return 'bg-gray-600';
}

function getEventBadge(type: string): string {
    if (type.includes('ERROR')) return 'bg-red-900/30 text-red-400';
    if (type.includes('DECISION')) return 'bg-green-900/30 text-green-400';
    if (type.includes('VOTE')) return 'bg-blue-900/30 text-blue-400';
    if (type.includes('TRANSITION')) return 'bg-purple-900/30 text-purple-400';
    return 'bg-slate-800 text-slate-400';
}

function renderPayloadSummary(event: CognitiveEvent): React.ReactNode {
    const p = event.payload || {};

    if (event.event_type === 'OBJECT_CREATED') {
        return <span>Initialized object v{p.version || 1}</span>;
    }

    if (event.event_type === 'STATE_TRANSITION') {
        return <span className="text-purple-300">Transitioned to {p.new_state}</span>;
    }

    if (event.event_type === 'VOTE_CAST') {
        return <span>User cast vote: <strong>{p.choice}</strong></span>;
    }

    if (event.event_type === 'DECISION_CREATED') {
        return <span className="text-green-300">Decision: {p.title}</span>;
    }

    // Default fallback
    return <span>{p.message || p.summary || 'Event recorded'}</span>;
}
